import { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react'

function useCustomSelect({ options, searchable, value, placeholder, disabled, onChange }) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [isHovered, setIsHovered] = useState(false)
  const [menuPosition, setMenuPosition] = useState(null)
  const containerRef = useRef(null)
  const controlRef = useRef(null)
  const menuRef = useRef(null) // Tracks the portaled menu for click-outside detection
  const inputRef = useRef(null)

  const filteredOptions = searchTerm
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options

  const displayValue = value ? value.label : placeholder

  const shouldShowHoverEffect = isHovered && !disabled && !isOpen

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
    setFocusedIndex(0)
  }

  const updateMenuPosition = useCallback(() => {
    if (!controlRef.current) return

    const rect = controlRef.current.getBoundingClientRect()
    setMenuPosition({
      top: rect.bottom + 8,
      left: rect.left,
      minWidth: rect.width,
    })
  }, [])

  const toggleOpen = () => {
    if (disabled) return

    if (!isOpen) {
      const selectedIndex = value ? filteredOptions.findIndex(opt => opt.value === value.value) : -1
      setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0)
      updateMenuPosition()
    }

    setIsOpen(!isOpen)

    if (!isOpen && searchable && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }

  const closeDropdown = () => {
    setIsOpen(false)
    setSearchTerm('')
    setFocusedIndex(-1)
  }

  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusedIndex(prev => {
          const next = prev + 1
          return next >= filteredOptions.length ? 0 : next
        })
        break

      case 'ArrowUp':
        e.preventDefault()
        setFocusedIndex(prev => {
          const next = prev - 1
          return next < 0 ? filteredOptions.length - 1 : next
        })
        break

      // Return focus to the control on keyboard close; otherwise the portaled menu
      // (which held the active element) unmounts and focus falls to <body>.
      case 'Enter':
        e.preventDefault()
        e.stopPropagation()
        if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
          onChange(filteredOptions[focusedIndex])
          closeDropdown()
          controlRef.current?.focus()
        }
        break

      // Stop propagation so the dismiss doesn't also trigger ancestor Escape handlers
      // (e.g. sidebar/gallery close) when this select is opened inside a focus trap.
      case 'Escape':
        e.preventDefault()
        e.stopPropagation()
        closeDropdown()
        controlRef.current?.focus()
        break

      default:
        break
    }
  }, [isOpen, focusedIndex, filteredOptions, onChange])

  useEffect(() => {
    if (!isOpen) return undefined

    // Capture phase so this runs before ancestor document-level keydown handlers
    // (e.g. Sidebar/Gallery Escape handlers) registered earlier in bubble phase.
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, handleKeyDown])

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideContainer = containerRef.current && containerRef.current.contains(event.target)
      const isInsideMenu = menuRef.current && menuRef.current.contains(event.target)

      if (!isInsideContainer && !isInsideMenu) {
        closeDropdown()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Keep the portaled menu aligned when the page or a scrollable ancestor scrolls/resizes
  useEffect(() => {
    if (!isOpen) return undefined

    const handleScrollOrResize = () => updateMenuPosition()

    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [isOpen, updateMenuPosition])

  // After the portaled menu mounts, clamp `left` so long labels don't push the right
  // edge off the viewport, and flip the menu above the control when there isn't
  // enough room below (prevents the list from being cut off on short screens).
  useLayoutEffect(() => {
    if (!isOpen || !menuRef.current || !controlRef.current || !menuPosition) return

    const menuRect = menuRef.current.getBoundingClientRect()
    const controlRect = controlRef.current.getBoundingClientRect()
    const margin = 8
    const gap = 8
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    const maxLeft = viewportWidth - margin - menuRect.width
    const clampedLeft = Math.max(margin, Math.min(menuPosition.left, maxLeft))

    const preferredBelow = controlRect.bottom + gap
    const preferredAbove = controlRect.top - gap - menuRect.height
    const belowFits = preferredBelow + menuRect.height <= viewportHeight - margin
    const aboveFits = preferredAbove >= margin

    let nextTop
    if (belowFits) {
      nextTop = preferredBelow
    } else if (aboveFits) {
      nextTop = preferredAbove
    } else {
      const spaceBelow = viewportHeight - preferredBelow - margin
      const spaceAbove = controlRect.top - gap - margin
      nextTop = spaceAbove > spaceBelow
        ? margin
        : Math.max(margin, viewportHeight - margin - menuRect.height)
    }

    if (clampedLeft !== menuPosition.left || nextTop !== menuPosition.top) {
      setMenuPosition(prev => ({ ...prev, left: clampedLeft, top: nextTop }))
    }
  }, [isOpen, menuPosition])

  return {
    isOpen,
    searchTerm,
    focusedIndex,
    filteredOptions,
    containerRef,
    controlRef,
    menuRef,
    inputRef,
    menuPosition,
    displayValue,
    shouldShowHoverEffect,
    setIsHovered,
    setFocusedIndex,
    handleSearchChange,
    toggleOpen,
    closeDropdown,
  }
}

export default useCustomSelect
